"use client";

import { useState, useEffect, useRef } from "react";
import { useTextScramble } from "@/components/marketing/hooks/useTextScramble";

const PLATFORMS = [
  { name: "Vinted", color: "#09B1BA" },
  { name: "Depop", color: "#FF2300" },
  { name: "Leboncoin", color: "#F56B2A" },
  { name: "Instagram", color: "#E1306C" },
  { name: "Vestiaire Co.", color: "#1A1A1A" },
];

/* Minimal, recognizable logo marks for each platform */
function PlatformLogo({
  name,
  color,
  isActive,
}: {
  name: string;
  color: string;
  isActive: boolean;
}) {
  const opacity = isActive ? 1 : 0.25;
  const size = 40;

  const logos: Record<string, React.ReactNode> = {
    Vinted: (
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" style={{ opacity, transition: "opacity 0.3s" }}>
        <path d="M12 8l8 24 8-24" stroke={color} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    Depop: (
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" style={{ opacity, transition: "opacity 0.3s" }}>
        <circle cx="20" cy="18" r="9" stroke={color} strokeWidth="3" />
        <line x1="20" y1="27" x2="20" y2="34" stroke={color} strokeWidth="3" strokeLinecap="round" />
      </svg>
    ),
    Leboncoin: (
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" style={{ opacity, transition: "opacity 0.3s" }}>
        <rect x="8" y="10" width="24" height="20" rx="3" stroke={color} strokeWidth="3" />
        <circle cx="20" cy="20" r="5" stroke={color} strokeWidth="2.5" />
      </svg>
    ),
    Instagram: (
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" style={{ opacity, transition: "opacity 0.3s" }}>
        <rect x="8" y="8" width="24" height="24" rx="6" stroke={color} strokeWidth="2.5" />
        <circle cx="20" cy="20" r="5.5" stroke={color} strokeWidth="2.5" />
        <circle cx="28" cy="12" r="1.8" fill={color} />
      </svg>
    ),
    "Vestiaire Co.": (
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" style={{ opacity, transition: "opacity 0.3s" }}>
        <path d="M10 12l10 16 10-16" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="10" y1="28" x2="30" y2="28" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    ),
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 10,
        cursor: "default",
        transition: "opacity 0.3s",
        minWidth: 80,
      }}
    >
      {logos[name]}
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: 0.5,
          color: isActive ? "#2C2520" : "rgba(44,37,32,0.25)",
          transition: "color 0.3s",
        }}
      >
        {name}
      </span>
    </div>
  );
}

export default function PlatformsSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const morphRef = useRef<HTMLSpanElement>(null);
  const scramble = useTextScramble();

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => {
        const next = (prev + 1) % PLATFORMS.length;
        scramble(morphRef.current, PLATFORMS[next].name, 400);
        return next;
      });
    }, 2500);

    return () => clearInterval(interval);
  }, [scramble]);

  return (
    <section
      id="platforms"
      style={{
        padding: "80px 5vw",
        textAlign: "center",
      }}
    >
      <h2
        style={{
          fontFamily: "var(--font-headline), Georgia, serif",
          fontStyle: "italic",
          fontSize: "clamp(48px, 7vw, 96px)",
          lineHeight: 1.1,
          marginBottom: 48,
        }}
      >
        Pr{"\u00EA"}te pour{" "}
        <span
          ref={morphRef}
          style={{
            color: "var(--color-mkt-terracotta, #D4613A)",
            display: "inline-block",
            minWidth: 200,
          }}
        >
          {PLATFORMS[0].name}
        </span>
      </h2>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 48,
          flexWrap: "wrap",
        }}
      >
        {PLATFORMS.map((platform, idx) => (
          <PlatformLogo
            key={platform.name}
            name={platform.name}
            color={platform.color}
            isActive={idx === activeIndex}
          />
        ))}
      </div>
    </section>
  );
}
